import { Modal, ModalOverlay, ModalContent, Flex, Image, Text, Box, useToast, Divider } from '@chakra-ui/react'
import { Button, Checkbox, Column, DateSelect, Dropdown, FormItem, Radio, Row } from '@ubirider/pick-component-library'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCards, useTicketing, useVehicles } from '../../../../context/DataProvider'
import { detectCardBrand } from '../../../../helpers/cards'
import { hasErrors, requiredValidation } from '../../../../helpers/validation'
import { editTicketSetting, purchaseTicket } from '../../../../api/ticketing'
import { settleUnpaidTrips } from '../../../../api/payment'
import { formatCurrency } from '../../../../helpers/currency'
import { DateTime } from 'luxon'

import CardIcon from '../../../../assets/credit_card.svg'
import CloseButton from '../../../../assets/cross.svg'
import CheckIcon from '../../../../assets/check_circle_white.svg'
import CloseIcon from '../../../../assets/cross_white_24.svg'

const SCROLLBAR_CSS = {
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: '#CACFD8', borderRadius: '4px' }
}

const Step = ({ step, current, label, onClick, done }) => {
  const isActive = current === step

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      align='center'
      gap='8px'
      as='button'
      onClick={onClick}
      _disabled={{ cursor: 'not-allowed', opacity: 0.6 }}
    >
      <Flex
        justify='center'
        align='center'
        w='32px'
        h='32px'
        bg={isActive ? '#8098F9' : done ? 'white' : '#CACFD8'}
        border={done && !isActive ? '1px solid #98A2B3' : 'none'}
        borderRadius='full'
      >
        <Text fontSize='14px' fontWeight='600' color={isActive ? 'white' : '#667085'}>
          {step}
        </Text>
      </Flex>
      <Text
        fontSize='13px'
        fontWeight={isActive ? '700' : '400'}
        color='#282828'
      >
        {label}
      </Text>
    </Flex>
  )
}

const Header = ({ currentStep, onClose, stepsDone, setStep }) => {
  const { t } = useTranslation()

  return (
    <Flex
      padding='16px 32px'
      bg='white'
      boxShadow='0px 4px 12px rgba(0,0,0,0.05)'
      direction='column'
      gap='20px'
      borderBottom='1px solid #E2E8F0'
      borderRadius={{ lg: '8px 8px 0 0' }}
      zIndex={3}
    >
      <Flex justify='space-between' align='center'>
        <Flex />
        <Text fontWeight='700' fontSize='20px' color='#001D20'>
          {t('acqua_vias.book_time_slot')}
        </Text>
        <Button type='neutralDark' style='typo' leftIconProps={{ src: CloseButton }} onClick={onClose} />
      </Flex>

      <Flex gap={{ sm: '32px', lg: '16px' }} justify='center' align='center'>
        <Step
          step={1}
          current={currentStep}
          label={t('acqua_vias.choose_date')}
          onClick={() => setStep(1)}
          done={stepsDone.includes(1)}
        />
        <Divider w='24px' borderColor='#CACFD8' display={{ sm: 'none', lg: 'flex' }} />
        <Step
          step={2}
          current={currentStep}
          label={t('acqua_vias.choose_trip')}
          onClick={() => stepsDone.includes(1) ? setStep(2) : null}
          done={stepsDone.includes(2)}
        />
        <Divider w='24px' borderColor='#CACFD8' display={{ sm: 'none', lg: 'flex' }} />
        <Step
          step={3}
          current={currentStep}
          label={t('acqua_vias.summary')}
          onClick={() => stepsDone.includes(2) ? setStep(3) : null}
          done={stepsDone.includes(3)}
        />
      </Flex>
    </Flex>
  )
}

export function NewTicketModal({ isOpen, onClose }) {
  const { t } = useTranslation()
  const toast = useToast()

  const windowSize = useRef([window.innerWidth, window.innerHeight])

  const { vehicles } = useVehicles()
  const { catalog, refetch } = useTicketing()
  const { cards } = useCards()

  const [formStep, setFormStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true)
  const [stepsDone, setStepsDone] = useState([])

  const {
    control,
    watch,
    reset,
    trigger,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm({
    mode: 'onChange',
  })

  const selectedTicketID = watch('trip')
  const selectedOutboundDate = watch('outboundDate')
  const selectedInboundDate = watch('inboundDate')
  const selectedOutboundTime = watch('outboundTime')
  const selectedInboundTime = watch('inboundTime')
  const paymentMethod = watch('paymentMethod')
  const checkedTerms = watch('terms')

  const [trip, mirroredTrip, origin, destination, price, mirroredPrice] = useMemo(() => {
    if (!selectedTicketID) return [null, null, null, null, 0, 0]

    const selectedJourney = catalog.find(ticket => ticket.ticketID === selectedTicketID)
    const mirroredJourney = selectedJourney?.mirroredTicket

    const price = selectedJourney?.price || 0
    const mirroredPrice = mirroredJourney?.price || 0

    const [first, third] = selectedJourney?.ticketName?.split(' - ') || []

    return [selectedJourney, mirroredJourney, first, third, price, mirroredPrice]
  }, [selectedTicketID, catalog])

  const outboundHours = useMemo(() => {
    const now = DateTime.now()
    const baseHours = Array.from({ length: 24 }, (_, i) =>
      now.set({ hour: i, minute: 0, second: 0, millisecond: 0 })
    )

    if (!selectedOutboundDate) return baseHours

    const selected = DateTime.isDateTime(selectedOutboundDate)
      ? selectedOutboundDate
      : DateTime.fromJSDate(new Date(selectedOutboundDate))

    const isToday = selected.toISODate() === now.toISODate()

    if (isToday) {
      return baseHours.filter(h => h.hour > now.hour)
    }

    return baseHours
  }, [selectedOutboundDate])

  const inboundHours = useMemo(() => {
    const now = DateTime.now()
    const baseHours = Array.from({ length: 24 }, (_, i) =>
      now.set({ hour: i, minute: 0, second: 0, millisecond: 0 })
    )

    if (!selectedInboundDate) return baseHours

    const selected = DateTime.isDateTime(selectedInboundDate)
      ? selectedInboundDate
      : DateTime.fromJSDate(new Date(selectedInboundDate))

    const isToday = selected.toISODate() === now.toISODate()

    if (isToday) {
      return baseHours.filter(h => h.hour > now.hour)
    }

    return baseHours
  }, [selectedInboundDate])

  const priceString = formatCurrency(price)
  const mirroredPriceString = formatCurrency(mirroredPrice)
  const totalPriceString = formatCurrency(price + mirroredPrice)

  useEffect(() => {
    if (isOpen) {
      reset({
        trip: null,
        vehicle: null,
        outboundDate: null,
        inboundDate: null,
        outboundTime: null,
        inboundTime: null,
        paymentMethod: null,
        terms: false
      })
      setFormStep(1)
      setStepsDone([])
    }
  }, [isOpen, reset])

  useEffect(() => {
    setIsScrolledToBottom(true)
  }, [formStep])

  const handleScroll = e => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    setIsScrolledToBottom(scrollHeight - scrollTop <= clientHeight + 1)
  }

  const nextStep = async (current) => {
    const isStepValid = await trigger()
    if (isStepValid) {
      setStepsDone(prev => [...new Set([...prev, current])])
      setFormStep(current + 1)
    }
  }

  const onSubmit = async data => {
    setLoading(true)
    try {
      const outboundTicket = {
        agencyID: trip.odPairs[0].agencyID,
        originID: trip.odPairs[0].originID,
        destinationID: trip.odPairs[0].destinationID,
        variationID: trip.variationID,
        quantity: 1,
      }

      const inboundTicket = {
        agencyID: mirroredTrip.odPairs[0].agencyID,
        originID: mirroredTrip.odPairs[0].originID,
        destinationID: mirroredTrip.odPairs[0].destinationID,
        variationID: mirroredTrip.variationID,
        quantity: 1,
      }

      const selectedCard = cards.find(card => card.recurringTransactionID === paymentMethod)
      const payload = {
        agencyID: trip.odPairs[0].agencyID,
        licensePlate: data.vehicle,
        cardNumber: selectedCard?.number,
      }

      const getValidity = (date, time) => {
        const tripDateTime = date.set({
          hour: time.hour,
          minute: time.minute,
          second: 0,
          millisecond: 0
        })

        const from = tripDateTime
          .minus({ hour: 1 })
          .toISO({ suppressMilliseconds: false })

        const until = tripDateTime
          .set({ minute: time.minute + 30 })
          .toISO({ suppressMilliseconds: false })

        return { type: 'calendar_range', from, until }
      }

      await Promise.all([
        editTicketSetting({
          id: selectedTicketID,
          validity: getValidity(data.outboundDate, data.outboundTime)
        }),
        editTicketSetting({
          id: mirroredTrip.ticketID,
          validity: getValidity(data.inboundDate, data.inboundTime)
        })
      ])

      const totalInCents = Math.round((price + mirroredPrice) * 100)

      await settleUnpaidTrips(
        totalInCents,
        selectedCard.recurringTransactionID,
        selectedCard.cardUniqueReference
      )

      await purchaseTicket([outboundTicket, inboundTicket], payload)

      toast({
        position: 'top',
        duration: 5000,
        isClosable: true,
        render: () => (
          <Box
            alignItems='center'
            color='white'
            bg='#01AE70'
            border='1px solid #067F5B'
            borderRadius='4px'
            p='16px'
          >
            <Flex justifyContent='space-between' gap='16px'>
              <Image src={CheckIcon} />
              <Text fontSize='14px' fontWeight='600'>
                Bilhetes comprados com sucesso
              </Text>
              <Image cursor='pointer' onClick={() => toast.closeAll()} src={CloseIcon} />
            </Flex>
          </Box>
        ),
      })

      refetch()
      onClose()
    } catch (err) {
      console.error('Purchase Error:', err)
      toast({
        position: 'top',
        duration: 5000,
        isClosable: true,
        render: () => (
          <Box
            alignItems='center'
            color='white'
            bg='#E8554F'
            border='1px solid #D23636'
            borderRadius='4px'
            p='16px'
          >
            <Flex justifyContent='space-between' gap='16px'>
              <Image src={CheckIcon} />
              <Text fontSize='14px' fontWeight='600'>
                Ocorreu um erro
              </Text>
              <Image cursor='pointer' onClick={() => toast.closeAll()} src={CloseIcon} />
            </Flex>
          </Box>
        ),
      })
      toast({
        title: 'Erro na compra',
        description: 'Não foi possível processar seu pedido. Por favor, tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      trapFocus={false}
      closeOnOverlayClick={false}
      autoFocus={false}
      scrollBehavior='inside'
      size={{ sm: 'full', md: 'full', lg: '', xl: '' }}
      isCentered={windowSize.current[0] >= 960}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius={{ sm: '0', lg: '8px' }}
        maxW={{ lg: '702px' }}
        mt={{ sm: '67px', lg: undefined }}
        h={{ sm: 'calc(100dvh - 67px)', lg: 'auto' }}
        minH={{ sm: 'calc(100dvh - 67px)', lg: 'auto' }}
        maxH={{ sm: 'calc(100dvh - 67px)', lg: 'auto' }}
        display='flex'
        flexDirection='column'
        overflow='hidden'
      >
        <Header currentStep={formStep} onClose={onClose} stepsDone={stepsDone} setStep={setFormStep} />

        <Flex
          as='form'
          onSubmit={handleSubmit(onSubmit)}
          direction='column'
          bg='#F9F9FB'
          overflow='hidden'
          position='relative'
          flex='1'
          minH='0'
        >
          <Box
            flex='1'
            overflowY='auto'
            border='1px solid #EAECF0'
            borderRadius='16px'
            w='100%'
            p={{ base: '16px', lg: '32px' }}
            css={SCROLLBAR_CSS}
            onScroll={handleScroll}
          >
            {formStep === 1 && (
              <Column css={{ gap: 24 }}>
                <Flex direction={{ sm: 'column', lg: 'row' }} gap='24px'>
                  <Controller
                    name='trip'
                    control={control}
                    rules={{ required: requiredValidation() }}
                    render={({ field: { value, onChange } }) => (
                      <FormItem required label={t('acqua_vias.journey')} error={errors.trip}>
                        <Dropdown
                          hasSearch
                          usePortal
                          cells={catalog.map(ticket => ({
                            label: ticket.ticketName,
                            onClick: () => onChange(ticket.ticketID),
                            selected: ticket.ticketID === value,
                          }))}
                          labelComponent={
                            value != null ? (
                              catalog.find(ticket => ticket.ticketID === value)?.ticketName
                            ) : (
                              <Text fontSize='16px' color='#98A2B3'>
                                {t('acqua_vias.choose_trip')}
                              </Text>
                            )
                          }
                          dropdownWidthBehaviour='force'
                          placement='bottom-start'
                        />
                      </FormItem>
                    )}
                  />

                  <Controller
                    name='vehicle'
                    control={control}
                    rules={{ required: requiredValidation() }}
                    render={({ field: { value, onChange } }) => (
                      <FormItem required label={t('acqua_vias.vehicle')} error={errors.vehicle}>
                        <Dropdown
                          hasSearch
                          usePortal
                          cells={vehicles
                            .sort((a, b) => a.model.localeCompare(b.model))
                            .map(vehicle => ({
                              label: vehicle.licensePlate,
                              onClick: () => onChange(vehicle.licensePlate),
                              selected: vehicle.licensePlate === value,
                            }))}
                          labelComponent={
                            value != null ? (
                              value
                            ) : (
                              <Text fontSize='16px' color='#98A2B3'>
                                Escolher veículo
                              </Text>
                            )
                          }
                          dropdownWidthBehaviour='force'
                          placement='bottom-start'
                        />
                      </FormItem>
                    )}
                  />
                </Flex>

                <Flex direction={{ sm: 'column', lg: 'row' }} gap='24px'>
                  <Controller
                    name='outboundDate'
                    control={control}
                    rules={{
                      required: requiredValidation(),
                    }}
                    render={({ field }) => (
                      <FormItem required label={t('acqua_vias.date_outbound')} error={errors.outboundDate}>
                        <DateSelect
                          {...field}
                          usePortal
                          minDate={DateTime.now()}
                          maxDate={selectedInboundDate ?? DateTime.now().plus({ month: 6 })}
                        />
                      </FormItem>
                    )}
                  />

                  <Controller
                    name='inboundDate'
                    control={control}
                    rules={{
                      required: requiredValidation(),
                    }}
                    render={({ field }) => (
                      <FormItem required label={t('acqua_vias.date_inbound')} error={errors.inboundDate}>
                        <DateSelect
                          {...field}
                          usePortal
                          minDate={selectedOutboundDate ?? DateTime.now()}
                          maxDate={DateTime.now().plus({ month: 6 })}
                        />
                      </FormItem>
                    )}
                  />
                </Flex>
              </Column>
            )}

            {formStep === 2 && (
              <Column css={{ gap: 32 }}>
                <Column css={{ gap: 24 }}>
                  <Column>
                    <Text fontSize='18px' fontWeight='700' color='#424D5F'>
                      {t('acqua_vias.date_outbound')}
                    </Text>
                    <Text>
                      {t('acqua_vias.from_to_date', {
                        origin,
                        destination,
                        date: selectedOutboundDate?.toFormat('dd/MM/yyyy'),
                      })}
                    </Text>
                  </Column>
                  <Controller
                    name='outboundTime'
                    control={control}
                    rules={{
                      required: requiredValidation(),
                    }}
                    render={({ field: { value, onChange } }) => (
                      <FormItem required label={t('acqua_vias.schedule')} error={errors.outboundTime}>
                        <Dropdown
                          usePortal
                          cells={outboundHours.map(hour => ({
                            label: hour.toFormat('HH:mm'),
                            onClick: () => onChange(hour),
                            selected: hour === value,
                          }))}
                          labelComponent={
                            value != null ? (
                              value.toFormat('HH:mm')
                            ) : (
                              <Text fontSize='16px' color='#98A2B3'>
                                Escolher horário
                              </Text>
                            )
                          }
                          placement='bottom-start'
                        />
                      </FormItem>
                    )}
                  />
                </Column>
                <Column css={{ gap: 24 }}>
                  <Column>
                    <Text fontSize='18px' fontWeight='700' color='#424D5F'>
                      {t('acqua_vias.date_inbound')}
                    </Text>
                    <Text>
                      {t('acqua_vias.from_to_date', {
                        origin: destination,
                        destination: origin,
                        date: selectedInboundDate?.toFormat('dd/MM/yyyy'),
                      })}
                    </Text>
                  </Column>
                  <Controller
                    name='inboundTime'
                    control={control}
                    rules={{
                      required: requiredValidation(),
                    }}
                    render={({ field: { value, onChange } }) => (
                      <FormItem required label={t('acqua_vias.schedule')} error={errors.inboundTime}>
                        <Dropdown
                          hasSearch
                          usePortal
                          cells={inboundHours.map(hour => ({
                            label: hour.toFormat('HH:mm'),
                            onClick: () => onChange(hour),
                            selected: hour === value,
                          }))}
                          labelComponent={
                            value != null ? (
                              value.toFormat('HH:mm')
                            ) : (
                              <Text fontSize='16px' color='#98A2B3'>
                                Escolher horário
                              </Text>
                            )
                          }
                          dropdownWidth={388}
                          dropdownWidthBehaviour='expand'
                          placement='bottom-start'
                        />
                      </FormItem>
                    )}
                  />
                </Column>
              </Column>
            )}

            {formStep === 3 && (
              <Column css={{ gap: 24 }}>
                <Column css={{ gap: 16 }}>
                  <Text fontWeight='600' fontSize={{ sm: '14px', lg: '16px' }}>
                    {t('acqua_vias.settle_pending_amount_dialog_choose_payment_method')}
                  </Text>
                  <Box bg='white' border='1px solid #EAECF0' borderRadius='16px' w='100%' overflow='hidden'>
                    <Controller
                      name='paymentMethod'
                      control={control}
                      rules={{ required: requiredValidation() }}
                      render={({ field: { value, onChange } }) => (
                        <>
                          {cards?.map((card, index) => (
                            <Flex
                              cursor='pointer'
                              align='center'
                              gap='16px'
                              padding='16px'
                              bg='white'
                              borderBottom={index === cards.length - 1 ? 'none' : '1px solid #EAECF0'}
                              key={`${index}_lg`}
                              onClick={() => onChange(card.recurringTransactionID)}
                            >
                              <Radio
                                checked={!!value && value === card.recurringTransactionID}
                                onChange={() => onChange(card.recurringTransactionID)}
                              />
                              <Column>
                                <Flex gap='16px' align='center'>
                                  <Text fontSize={{ sm: '14px', lg: '16px' }} fontWeight='700'>
                                    {detectCardBrand(card.number.slice(0, 6)).label} •••• {card.number.slice(-4)}
                                  </Text>
                                </Flex>
                                <Text fontSize={{ sm: '14px', lg: '16px' }} fontWeight='600' color='neutral.600'>
                                  {`${card.expiration.substring(5, 7)}/${card.expiration.substring(2, 4)}`}
                                </Text>
                              </Column>
                            </Flex>
                          ))}
                        </>
                      )}
                    />
                  </Box>
                </Column>

                <Box p='24px' bg='white' border='1px solid #EAECF0' borderRadius='16px' w='100%'>
                  <Column css={{ gap: 24 }}>
                    <Column css={{ gap: 16 }}>
                      <Text fontSize='14px' fontWeight='700' color='#444CE7' textTransform='uppercase'>
                        {t('acqua_vias.outbound')}
                      </Text>
                      <Column css={{ gap: 8 }}>
                        <Row css={{ justifyContent: 'space-between' }}>
                          <Text fontSize='14px' fontWeight='700'>
                            {origin} - {destination}
                          </Text>
                          <Text fontSize='14px'>R$ {priceString}</Text>
                        </Row>
                        <Text fontSize='14px'>
                          {selectedOutboundDate?.setLocale('pt-BR').toFormat('d MMMM')},{' '}
                          {selectedOutboundTime?.toFormat('HH:mm')}
                        </Text>
                      </Column>
                    </Column>
                    <Divider />
                    <Column css={{ gap: 16 }}>
                      <Text fontSize='14px' fontWeight='700' color='#444CE7' textTransform='uppercase'>
                        {t('acqua_vias.inbound')}
                      </Text>
                      <Column css={{ gap: 8 }}>
                        <Row css={{ justifyContent: 'space-between' }}>
                          <Text fontSize='14px' fontWeight='700'>
                            {destination} - {origin}
                          </Text>
                          <Text fontSize='14px'>R$ {mirroredPriceString}</Text>
                        </Row>
                        <Text fontSize='14px'>
                          {selectedInboundDate?.setLocale('pt-BR').toFormat('d MMMM')},{' '}
                          {selectedInboundTime?.toFormat('HH:mm')}
                        </Text>
                      </Column>
                    </Column>
                  </Column>
                </Box>

                <Box bg='white' p='16px' border='1px solid #EAECF0' borderRadius='16px' w='100%'>
                  <Column css={{ gap: 8 }}>
                    <Row css={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text fontSize='14px' color='#6A7282'>
                        {t('acqua_vias.subtotal')}
                      </Text>
                      <Text fontSize='14px' fontWeight='600'>
                        R$ {totalPriceString}
                      </Text>
                    </Row>
                    <Row css={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text fontSize='14px' color='#6A7282'>
                        {t('acqua_vias.discount')}
                      </Text>
                      <Text fontSize='14px' fontWeight='600'>
                        R$ 0.00
                      </Text>
                    </Row>
                    <Row
                      css={{
                        borderTop: '1px solid #E5E7EB',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pt: '8px',
                      }}
                    >
                      <Text fontSize='16px' fontWeight='700'>
                        {t('acqua_vias.last_trips_total')}
                      </Text>
                      <Text fontSize='14px' fontWeight='700' color='#444CE7'>
                        R$ {totalPriceString}
                      </Text>
                    </Row>
                  </Column>
                </Box>

                <Column css={{ gap: 16 }}>
                  <Text fontSize='14px' fontWeight='700'>
                    {t('acqua_vias.sale_terms_and_conditions')}
                  </Text>
                  <Controller
                    name='terms'
                    control={control}
                    rules={{ required: requiredValidation() }}
                    render={({ field: { value, onChange } }) => (
                      <Row css={{ gap: 16, cursor: 'pointer' }} onClick={() => onChange(!value)}>
                        <Checkbox checked={!!value} onChange={onChange} />
                        <Text fontSize='14px'>Concordo com os termos e condições.</Text>
                      </Row>
                    )}
                  />
                </Column>
              </Column>
            )}
          </Box>

          <Flex
            p='16px 32px'
            bg={isScrolledToBottom ? '#F9F9FB' : 'white'}
            boxShadow={isScrolledToBottom ? 'none' : '0px -4px 12px rgba(0,0,0,0.05)'}
            justify='flex-end'
            gap='12px'
            transition='all 0.2s'
            borderTop='1px solid #EAECF0'
            position='relative'
            bottom='auto'
            left={0}
            right={0}
            zIndex={500}
          >
            {formStep > 1 && (
              <Button
                style='outline'
                type='neutralLight'
                label='Voltar'
                onClick={() => setFormStep(prev => prev - 1)}
              />
            )}
            {formStep < 3 ? (
              <Button
                style='solid'
                type='secondary'
                label={formStep === 1 ? 'Procurar viagens' : 'Continuar'}
                disabled={hasErrors(errors) || !isDirty}
                onClick={() => nextStep(formStep)}
              />
            ) : (
              <Button
                submit
                style='solid'
                type='secondary'
                label={t('acqua_vias.pay_amount', { amount: `R$ ${totalPriceString}` })}
                leftIconProps={{ src: CardIcon }}
                disabled={!paymentMethod || !checkedTerms}
                loading={loading}
              />
            )}
          </Flex>
        </Flex>
      </ModalContent>
    </Modal>
  )
}